import React, { useState } from 'react';
import { Button, message, Modal, Upload } from 'antd';
import { CameraOutlined, UploadOutlined } from '@ant-design/icons';
import { useDocument } from '../../Pages/SpreadsheetContainer/documentInfoContex';
import { templateService } from '../../utils/api';
import { useCompressionImageUpload } from '../../hooks/useCompressionImageUpload';
import CompressionConfirmModal from '../ImageCompressionModal/CompressionConfirmModal';
import { dataUrlToFile } from '../../utils/imageUpload';
import './TemplateThumbnail.css';

const TemplateThumbnail = () => {
  const { isTemplateDocument, templateData, setTemplateData, docID, setSavingData } = useDocument();
  const { uploadImageFile, compressionModalProps } = useCompressionImageUpload({
    zIndex: 10050,
    uploadSuccessMessage: 'Image uploaded successfully',
  });
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [customUrl, setCustomUrl] = useState(null);
  const [imageBusy, setImageBusy] = useState(false);
  const existingThumbnail = templateData?.thumbnail || null;

  const handleOpenModal = () => {
    setPreviewUrl(existingThumbnail);
    setIsModalVisible(true);
  };

  const handleCapture = async () => {
    try {
      const canvas = document.querySelector('.x-spreadsheet-table');
      if (!canvas) {
        message.error('Spreadsheet canvas not found');
        return;
      }
      setImageBusy(true);
      const thumbnailDataUrl = canvas.toDataURL('image/png');
      const file = await dataUrlToFile(thumbnailDataUrl, 'thumbnail-capture.png');
      const url = await uploadImageFile(file);
      setCustomUrl(null);
      setPreviewUrl(url);
    } catch (error) {
      const cancelled = String(error?.message || '').toLowerCase().includes('cancel');
      if (!cancelled) {
        console.error('Failed to capture thumbnail:', error);
        message.error(error?.message || 'Failed to capture thumbnail');
      }
    } finally {
      setImageBusy(false);
    }
  };

  const handleConfirmUpdate = async () => {
    try {
      const thumbnailUrl = customUrl || previewUrl;
      const res = await templateService.updateTemplate({ templateId: docID, data: { thumbnail: thumbnailUrl, docType: 'milegrid' }, dispatch: setSavingData });
      message.success('Thumbnail saved successfully');
      setIsModalVisible(false);
      setPreviewUrl(null);
      setCustomUrl(null);
      // Update templateData
      if (res?.data?.data) {
        setTemplateData(res.data.data);
      }
    } catch (error) {
      console.error('Failed to save thumbnail:', error);
      message.error('Failed to save thumbnail');
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setPreviewUrl(null);
    setCustomUrl(null);
  };

  const beforeUploadThumbnail = async (file) => {
    if (!file.type?.startsWith?.('image/')) {
      message.error('Please select an image file');
      return false;
    }
    setImageBusy(true);
    try {
      const url = await uploadImageFile(file);
      setCustomUrl(url);
    } catch (error) {
      const cancelled = String(error?.message || '').toLowerCase().includes('cancel');
      if (!cancelled) {
        message.error(error?.message || 'Thumbnail upload failed');
      }
    } finally {
      setImageBusy(false);
    }
    return false;
  };

  if (!isTemplateDocument) return null;

  return (
    <>
      <Button
        type="text"
        icon={<CameraOutlined className="thumbnail-button-icon" />}
        onClick={handleOpenModal}
        title="Save Thumbnail"
        size="small"
        className="thumbnail-button"
      />
      <Modal
        title={<div className="thumbnail-modal-header"><CameraOutlined className="thumbnail-modal-icon" />Thumbnail Preview</div>}
        visible={isModalVisible}
        onCancel={handleCancel}
        className="thumbnail-modal"
        width={560}
        footer={[
          <Button key="cancel" onClick={handleCancel} className="cancel-btn">
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={handleConfirmUpdate}
            className="update-thumbnail-btn"
            disabled={imageBusy}
          >
            Save Thumbnail
          </Button>,
        ]}
      >
        <div className="thumbnail-content">
          <div className="thumbnail-preview-container" role="img" aria-label="thumbnail-preview">
            {(customUrl || previewUrl) ? (
              <img src={customUrl || previewUrl} alt="Thumbnail Preview" className="thumbnail-preview" />
            ) : (
              <div className="thumbnail-placeholder">
                <div className="placeholder-icon">📄</div>
                <p>No thumbnail yet</p>
              </div>
            )}
          </div>
          <div className="upload-section">
            <div className="upload-label">Upload a custom thumbnail</div>
            <Upload
              beforeUpload={beforeUploadThumbnail}
              showUploadList={false}
              accept="image/*"
            >
              <Button icon={<UploadOutlined />} className="upload-btn" loading={imageBusy} disabled={imageBusy}>
                Upload Custom Thumbnail
              </Button>
            </Upload>
            <div className="capture-label">Capture current view</div>
            <Button
              icon={<CameraOutlined />}
              onClick={handleCapture}
              className="capture-btn"
              loading={imageBusy}
              disabled={imageBusy}
            >
              Capture Current View
            </Button>
          </div>
        </div>
      </Modal>
      <CompressionConfirmModal {...compressionModalProps} />
    </>
  );
};

export default TemplateThumbnail;